import React, { useState, useCallback, forwardRef, useImperativeHandle } from "react";
import { MapPin, AlertCircle } from "lucide-react";
import type { ValidateResult } from "../../utils/addressValidation";

const inputClass =
  "w-full px-3 py-2.5 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 focus:ring-2 focus:ring-blue-500 outline-none";

export interface AddressInputProps {}

export interface AddressInputHandle {
  validate: () => Promise<ValidateResult>;
}

/**
 * Saisie d'adresse en 3 champs : num+voie | code postal | commune.
 * Aucune vérification BAN : l'adresse saisie est enregistrée telle quelle.
 */
export const AddressInput = forwardRef<AddressInputHandle, AddressInputProps>(
  function AddressInput(_props: AddressInputProps, ref) {
    const [voie, setVoie] = useState("");
    const [postcode, setPostcode] = useState("");
    const [city, setCity] = useState("");
    const [error, setError] = useState<string | null>(null);

    const clearError = useCallback(() => setError(null), []);

    const handleVoieChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setVoie(e.target.value);
      clearError();
    };
    const handlePostcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value.replace(/\D/g, "").slice(0, 5);
      setPostcode(v);
      clearError();
    };
    const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setCity(e.target.value);
      clearError();
    };

    const validate = useCallback(async (): Promise<ValidateResult> => {
      setError(null);
      const v = voie.trim();
      const p = postcode.trim().replace(/\s/g, "");
      const c = city.trim();
      if (!v || !p || !c) {
        const err = "Veuillez remplir le numéro/voie, le code postal et la commune.";
        setError(err);
        return { ok: false, error: err };
      }
      const label = `${v}, ${p} ${c}`;
      return { ok: true, label };
    }, [voie, postcode, city]);

    useImperativeHandle(ref, () => ({ validate }), [validate]);

    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-gray-500 dark:text-slate-400 mb-1">
          <MapPin size={14} />
          <span className="text-xs font-medium uppercase">Adresse (obligatoire)</span>
        </div>

        <div className="space-y-2">
          <input
            required
            type="text"
            placeholder="Numéro et nom de voie (ex. 10 rue de la Paix)"
            value={voie}
            onChange={handleVoieChange}
            className={inputClass}
            aria-invalid={!!error}
          />
          <input
            required
            type="text"
            inputMode="numeric"
            maxLength={5}
            placeholder="Code postal (5 chiffres)"
            value={postcode}
            onChange={handlePostcodeChange}
            className={inputClass}
            aria-invalid={!!error}
          />
          <input
            required
            type="text"
            placeholder="Commune"
            value={city}
            onChange={handleCityChange}
            className={inputClass}
            aria-invalid={!!error}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="flex items-start gap-2 p-2.5 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-xl text-red-700 dark:text-red-300 text-sm"
          >
            <AlertCircle size={16} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}
      </div>
    );
  }
);
